import React, { Component, Fragment, useEffect, } from 'react';
import { useToggle, useAsync, useList, } from 'react-use';
import { isEmpty, get, orderBy, keyBy, sortBy, omit, difference, } from 'lodash';
import { Input, } from 'reactstrap';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import numeral from 'numeral';

import { batch } from './../shared/firebase';
import { accountItemCategoryNames, } from '../shared/config';
import { floatFormat } from './../util';
import firebase, { functions } from './../firebase';
import { generateRows, rowsForExport, computeAlerts, } from '../shared/lib/individualPkg';
import { uniqAccountName, } from './../shared/util';
import useCollectionSubscription from './hooks/useCollectionSubscription';
import useDocumentSubscription from './hooks/useDocumentSubscription';
import usePkgItems from './hooks/usePkgItems';
import ModelFormModal from './modals/ModelFormModal';
import ProgressButton from './ProgressButton';
import ChangesTableImportModalButton from './ChangesTableImportModalButton';
import AddButton from './AddButton';
import EditButton from './EditButton';
import ExportButton from './ExportButton';
import ImportButton from './ImportButton';
import DeleteButton from './DeleteButton';
import AmountWithExchange from './AmountWithExchange';
import Alerts from './Alerts';
import ChangeFieldsModalButton from './ChangeFieldsModalButton';

const { entries } = Object;
const db = firebase.firestore();

export default function IndividualChangesTableSection (props) {
  const { collectionName, section, title, fields, changeFieldNames, user, company, period, yearMonth, filteredYearMonths, relatedCompanies, accountItems, consolidationAccountItems, itemTrials, trials, startYearMonthTrials, subsidiaryId, filterTrials, relatedCompany, prevCr, cr, isLockedMonth, isParent, modelType, customChangeFields = [], savedOrder, hiddenKeys = [], } = props;
  const itemsRef = company.ref.collection(collectionName);
  const fieldsArg = { accountItems, customChangeFields, savedOrder, hiddenKeys };
  const { items } = usePkgItems(company, relatedCompany, subsidiaryId, accountItems, collectionName, [], 'closing', changeFieldNames, filteredYearMonths, customChangeFields);
  const accountItemsByName = keyBy(accountItems, 'name');
  const consolidationAccountItemsById = keyBy(consolidationAccountItems, 'id');
  const trialBalances = trials.flatMap(_ => _.balances);
  const prevEndTrialBalances = startYearMonthTrials.flatMap(_ => _.balances);
  const prevEndTrialBalancesByUniqName = keyBy(prevEndTrialBalances, _ => uniqAccountName(relatedCompany, _.account_item_name, _.key));
  const importSetting = useDocumentSubscription(company.ref.collection('changesTableImportSettings').doc(`${collectionName}_${subsidiaryId}`), [company, collectionName, subsidiaryId], { onError: _ => console.log(_) });
  const rows = generateRows(relatedCompany, items || [], accountItems, changeFieldNames, prevCr, cr, trials);
  const [selectedIds, { push: selectId, removeAt: removeSelectedIdAt, set: setSelectedIds, }] = useList([]);
  const unselectId = (_) => removeSelectedIdAt(selectedIds.indexOf(_));
  const isSelecting = selectedIds.length > 0;
  const isAllSelected = rows.length > 0 && difference(rows.map(_ => _.id), selectedIds).length === 0;
  const onClickBatchDelete = async () => {
    if(!window.confirm(`${selectedIds.length}件を削除します。よろしいですか？`)) return;

    await batch(db, selectedIds, (b, _) => b.delete(itemsRef.doc(_)));
    toast.success('削除しました');
    setSelectedIds([]);
  };
  const processImportRow = (batch, row) => {
    const { accountItemName, opening, ...changeFields } = row;
    const accountItemId = accountItemsByName[accountItemName]?.id;
    if(!accountItemId) return;

    const data = {
      accountItemId,
      opening: numeral(opening).value() || 0,
      ...changeFieldNames.reduce((x, fieldName) => ({
        ...x,
        [fieldName]: numeral(changeFields[fieldName]).value() || 0,
      }), {}),
      subsidiaryId,
      period,
      yearMonth,
      createdAt: new Date(),
    };

    batch.set(itemsRef.doc(), data);
  };
  const alerts = computeAlerts(relatedCompany, items || [], accountItems, changeFieldNames, prevCr, cr, trials);

  return (
    <div>
      <h5>{title}</h5>
      <Alerts alerts={alerts} />
      <div className="mb-3 d-flex justify-content-end gap-1">
        {
          isSelecting && (
            <Fragment>
              <span className="align-self-center">{selectedIds.length} 件を選択中</span>
              <ProgressButton color="danger" process={onClickBatchDelete} disabled={isLockedMonth}>
                削除
              </ProgressButton>
            </Fragment>
          )
        }
        <ChangesTableImportModalButton
          company={company}
          collectionName={collectionName}
          itemsRef={itemsRef}
          fields={fields}
          fieldsArg={fieldsArg}
          filterTrials={filterTrials}
          itemTrials={itemTrials}
          trialBalances={trialBalances}
          accountItems={accountItems}
          accountItemsByName={accountItemsByName}
          consolidationAccountItemsById={consolidationAccountItemsById}
          prevEndTrialBalancesByUniqName={prevEndTrialBalancesByUniqName}
          relatedCompany={relatedCompany}
          subsidiaryId={subsidiaryId}
          period={period}
          yearMonth={yearMonth}
          existingItems={items || []}
          savedSetting={importSetting}
          disabled={isLockedMonth}
        />
        <ExportButton screenName={`${section}_${title}`} targetCompany={relatedCompany} rows={rowsForExport(relatedCompany, items, accountItems, changeFieldNames, prevCr, cr, trials)} />
        <ImportButton processRow={processImportRow} importKey={`${collectionName}-${subsidiaryId}-${yearMonth}`} disabled={isLockedMonth} />
        {modelType && <ChangeFieldsModalButton company={company} user={user} modelType={modelType} customChangeFields={customChangeFields} savedOrder={savedOrder} hiddenKeys={hiddenKeys} isParent={isParent} />}
        <AddButton itemRef={itemsRef.doc()} processValues={_ => ({ ..._, subsidiaryId, period, yearMonth, })} FormModal={ModelFormModal} formProps={{ title, documentName: 'generalPkgItem', fields: fields({ accountItems, otherItems: rows, customChangeFields, savedOrder, hiddenKeys, }) }} disabled={isLockedMonth} />
      </div>
      <div className="overflow-auto" style={{ maxHeight: 'min(800px, 80vh)', }}>
        <table className="table sticky-table table-bordered">
          <thead className="thead-light text-center">
            <tr>
              <th className="sticky" style={{ width: 40, zIndex: 3, }}>
                <Input
                  type="checkbox"
                  className="position-relative m-0"
                  checked={isAllSelected}
                  onChange={_ => isAllSelected ? setSelectedIds([]) : setSelectedIds(rows.map(_ => _.id))}
                />
              </th>
              {
                entries(fields(fieldsArg)).map(([fieldName, { label }], i) => {
                  return (
                    <th style={{ minWidth: 150, ...(i === 0 && { position: 'sticky', left: 40, zIndex: 3, }) }} key={fieldName}>
                      {label}
                    </th>
                  );
                })
              }
              <th style={{ minWidth: 150 }}>
                為替差額
              </th>
              <th style={{ minWidth: 150 }}>
                期末
              </th>
              <th style={{ minWidth: 150 }}>
                試算表残高
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody className="thead-light">
            {
              rows.map((item) => {
                const { id, ref, accountItem, exchangedAmounts, closing, exchangeDiff, trialAmount, } = item;

                return (
                  <tr key={id}>
                    <th className="sticky" style={{ zIndex: 1, }}>
                      <Input
                        type="checkbox"
                        className="position-relative m-0"
                        checked={selectedIds.includes(id)}
                        onChange={_ => selectedIds.includes(id) ? unselectId(id) : selectId(id)}
                      />
                    </th>
                    <th style={{ position: 'sticky', left: 40, zIndex: 1, }}>
                      {accountItem && accountItem.name}
                    </th>
                    {
                      entries(omit(fields(fieldsArg), ['accountItemId'])).map(([fieldName, { label }]) => {
                        const isOpening = fieldName === 'opening';
                        const amount = item[fieldName] || 0;
                        const exchangedAmount = isOpening ? amount * prevCr : exchangedAmounts[fieldName];
                        return (
                          <td key={fieldName} className="text-right">
                            <AmountWithExchange currency={relatedCompany.currency} amounts={[amount, exchangedAmount]} />
                          </td>
                        );
                      })
                    }
                    <td className="text-right">
                      <AmountWithExchange currency={relatedCompany.currency} amounts={[0, exchangeDiff]} />
                    </td>
                    <td className={classnames('text-right', { 'table-danger': floatFormat(closing) !== floatFormat(trialAmount) })}>
                      <AmountWithExchange currency={relatedCompany.currency} amounts={[closing, closing * cr]} />
                    </td>
                    <td className="text-right">
                      <AmountWithExchange currency={relatedCompany.currency} amounts={[trialAmount, trialAmount * cr]} />
                    </td>
                    <td className="text-nowrap text-right">
                      <EditButton itemRef={ref} FormModal={ModelFormModal} formProps={{ title, documentName: 'generalPkgItem', fields: fields({ accountItems, otherItems: rows.filter(_ => _.id !== id), customChangeFields, savedOrder, hiddenKeys, }), }} disabled={isLockedMonth} />
                      <DeleteButton item={item} itemRef={ref} className="ml-2" disabled={isLockedMonth} />
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}
